import { Question } from '../../models/Question';
import {
  FileContainer,
  UploadedFilesContainer
} from '../../views/task-view/task-details-view2/task-files-step/TaskFilesStep';
import FilePreview from '../file-preview/FilePreview';
import Button from '../button/Button';
import styled from 'styled-components';
import { getGuid } from '../../utils/guidGenerating';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CameraIcon } from '../../assets/icons/CameraIcon';
import { FileAnswerDTO, FileParameter } from '../../api/api';
import QuestionHeader from '../question-header/QuestionHeader';
import TaskChecklistService from '../../services/TaskChecklistService';
import TaskService from '../../services/TaskService';
import { log } from '../../utils/logging/log';
import { DownloadInfo, ImageType } from '../../models/DownloadInfo';
import ImageGalleryDialog from '../file-preview/ImageGalleryDialog';
import QAService from '../../services/QAService';
import NotificationService from '../../services/NotificationService';
import { MIN_IMAGE_DIMENSION_PX, isImageTooSmall } from '../../utils/fileUtils';

interface Props {
  question: Question;
  handleSaveFile?: (file: FileParameter[], answerId: number, callback?: () => void) => void;
  taskId?: number;
  handleRemove?: (fileAnswerId: number, questionId: number, taskId: number) => void;
  required?: boolean;
  showMandatoryFieldError?: boolean;
  readOnly?: boolean;
}

const QuestionFile = (props: Props) => {
  const { question, handleSaveFile, taskId, handleRemove, required, showMandatoryFieldError, readOnly } = props;
  const [answerId, setAnswerId] = useState(question.id);
  const [uploading, setUploading] = useState(false);
  const htmlId = question.id?.toString() ?? question.sectionQuestionId + getGuid();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [images, setImages] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const findAvailableFilename = (files: FileAnswerDTO[], originalFilename: string) => {
    files = files || [];
    let newFilename = originalFilename;
    let index = 1;
    const dotIndex = originalFilename.lastIndexOf('.');
    const extension = dotIndex !== -1 ? originalFilename.slice(dotIndex) : '';

    while (files.some((file) => file.fileName === newFilename)) {
      newFilename = `${originalFilename.slice(0, dotIndex)} (${index})${extension}`;
      index++;
    }
    return newFilename;
  };

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!!event.target.files) {
      const selectedFiles = Array.from(event.target.files);

      const tooSmallFlags = await Promise.all(selectedFiles.map((obj) => isImageTooSmall(obj)));
      const tooSmallFiles = selectedFiles.filter((_, i) => tooSmallFlags[i]);
      const validFiles = selectedFiles.filter((_, i) => !tooSmallFlags[i]);

      if (tooSmallFiles.length > 0) {
        NotificationService.error(
          `Billedet skal være større end ${MIN_IMAGE_DIMENSION_PX}×${MIN_IMAGE_DIMENSION_PX} pixels for at sikre tilstrækkelig billedkvalitet - følgende blev ikke gemt: ${tooSmallFiles
            .map((obj) => obj.name)
            .join(', ')}`
        );
      }

      if (validFiles.length === 0) {
        resetInput();
        return;
      }

      const files = validFiles.map((obj) => {
        let availableFilename = findAvailableFilename(question?.value, obj.name);
        return {
          data: obj,
          fileName: availableFilename
        } as FileParameter;
      });

      setUploading(true);
      handleSaveFile && handleSaveFile(files, answerId ?? -1, () => setUploading(false));
    }
  };

  const handleDownload = useCallback(
    (filename: string): DownloadInfo => {
      return { imageType: ImageType.QADownloadInfo, taskId, filename, answerId };
    },
    [taskId, answerId]
  );

  const getAnswerId = () => {
    if (answerId === null || answerId === undefined) {
      TaskChecklistService.updateAnswer({ workTaskId: taskId, sectionQuestionId: question.sectionQuestionId }).then(
        (res) => {
          setAnswerId(res[0].answerId);
        }
      );
    }
  };

  const getThumbnail = useCallback(
    async (fileName: string): Promise<string> => {
      const filename = fileName.substring(0, fileName.lastIndexOf('.'));
      return TaskService.getThumbNail(taskId ?? -1, filename, answerId ?? -1)
        .then((res) => URL.createObjectURL(res.data))
        .catch((err) => {
          log(err);
          return '';
        });
    },
    [taskId, answerId]
  );

  const openGallery = (filename: string) => {
    const index = question.value.findIndex((img: any) => img.fileName === filename);
    if (index !== -1) {
      setGalleryIndex(index);
      setGalleryOpen(true);
    }
  };

  useEffect(() => {
    if (!question.value?.length || !taskId || !answerId) return;

    const promises = question.value.map((image: any) => QAService.GetQAFile2(taskId, image.fileName, answerId));

    Promise.allSettled(promises).then((data) => {
      const newImages = data.filter((d) => d.status === 'fulfilled').map((d) => URL.createObjectURL((d as any).value.data));

      setImages(newImages);
    });
  }, [answerId, question.value, taskId]);

  return (
    <Container>
      <QuestionHeader
        text={question.text}
        required={required}
        helpText={question.helpText}
        error={showMandatoryFieldError && required && question.value.length === 0}
        readOnly={readOnly}
      />
      {!readOnly && (
        <InputContainer>
          <StyledInput
            ref={inputRef}
            accept={'image/*'}
            id={htmlId}
            multiple
            type="file"
            onChange={handleUploadFile}
            onClick={resetInput}
            data-testid="fileInput"
          />
          <Label htmlFor={htmlId}>
            <Button variant="secondary" element="span" isLoading={uploading} onClick={() => getAnswerId()}>
              <CameraIcon /> Upload
            </Button>
          </Label>
        </InputContainer>
      )}
      {question.value && (
        <UploadedFilesContainer>
          {question?.value.length > 0 &&
            taskId &&
            question?.value.map((obj: FileAnswerDTO, i: any) => {
              return (
                <FileContainer key={obj.fileName}>
                  <FilePreview
                    name={obj.fileName ?? ''}
                    date={obj.uploadedTime}
                    uploadedBy={obj.uploadedBy}
                    handleRemove={
                      readOnly
                        ? undefined
                        : () => handleRemove && question.id && handleRemove(obj.fileAnswerId ?? -1, question.id, taskId)
                    }
                    handleDownload={(fileName) => handleDownload(fileName)}
                    handleThumbnail={() => getThumbnail(obj.fileName ?? '')}
                    isImage
                    onClick={() => {
                      openGallery(obj.fileName ?? '');
                    }}
                  />
                </FileContainer>
              );
            })}
        </UploadedFilesContainer>
      )}
      {galleryOpen && (
        <ImageGalleryDialog initialIndex={galleryIndex} onClose={() => setGalleryOpen(false)} images={images} />
      )}
    </Container>
  );
};

const InputContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  display: flex;
`;

export const StyledInput = styled.input`
  display: none;
`;

export default QuestionFile;
